/**
 * BNPL billing detail drawer (ERS-5, rebuilt ERS-61)
 *
 * "Ablauf nach deinem Kursabschluss im Detail" drawer, opened from the BNPL explain screen. Rebuilt
 * against the four Figma frames (ERS-61): title, timeline,
 * FAQ set and order, shared `OnboardingSupportHint` support card, and a pinned `Schließen` footer.
 */

import { useId, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { space } from '../../../../styles/onboardingUiTokens';
import { Button } from '../actions/Button';
import {
  BNPL_DRAWER_CLOSE_LABEL,
  BNPL_DRAWER_FAQ_HEADING,
  BNPL_DRAWER_TITLE,
  buildBnplDrawerFaq,
  buildBnplDrawerSteps,
} from '../../insurance/bnplCopy';
import type { InsuranceCoverage } from '../../insurance/metaBuilders';
import { Accordion } from '../elements/Accordion';
import { BottomDrawer } from '../elements/BottomDrawer';
import { OnboardingSupportHint } from '../layout/OnboardingSupportHint';
import { H1, H3 } from '../typography/HeadingLevels';
import { ListTimeline, ListTimelineItem } from './ListTimeline';

/** The bonus step shows a gift instead of its step number (matches the explain screen's bullet). */
const BONUS_MARKER = (
  <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
    🎁
  </span>
);

const FaqSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
  margin-top: ${space.lg};
`;

export type InsuranceBnplBillingDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  insuranceShortName: string;
  coverage: InsuranceCoverage;
  hasBonus: boolean;
  /** The insurer's own `bonus.description`, when it has one (F3). */
  bonusDescription?: string;
  /** True when the insurer makes the patient hand in the course (`isPatientSubmittingInsurer`). */
  patientSubmits: boolean;
};

export function InsuranceBnplBillingDetailDrawer({
  open,
  onClose,
  insuranceShortName,
  coverage,
  hasBonus,
  bonusDescription,
  patientSubmits,
}: InsuranceBnplBillingDetailDrawerProps) {
  const titleId = useId();

  const steps = useMemo(
    () => buildBnplDrawerSteps({ insuranceShortName, coverage, hasBonus, bonusDescription, patientSubmits }),
    [insuranceShortName, coverage, hasBonus, bonusDescription, patientSubmits],
  );
  const faq = useMemo(() => buildBnplDrawerFaq(insuranceShortName, coverage), [insuranceShortName, coverage]);

  return (
    <BottomDrawer
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      gap={space.md}
      // The support card sits directly above the pinned footer, and it gives up its own bottom
      // margin here (`bottomSpacing="none"`) so the drawer owns the rhythm. The drawer's default
      // `space.md` left it almost touching the footer's divider, so it takes over the `space.lg`
      // the card would otherwise have contributed.
      contentPadding={space.lg}
      footer={
        <Button variant="primary" onClick={onClose} style={{ width: '100%' }}>
          {BNPL_DRAWER_CLOSE_LABEL}
        </Button>
      }
    >
      <H1 id={titleId}>{BNPL_DRAWER_TITLE}</H1>

      <ListTimeline>
        {steps.map((step, index) => (
          <ListTimelineItem
            key={step.key}
            title={step.title}
            highlighted={index === 0 || step.isBonus}
            highlightColor={step.isBonus ? colors.success : undefined}
            marker={step.isBonus ? BONUS_MARKER : undefined}
          >
            {step.body}
          </ListTimelineItem>
        ))}
      </ListTimeline>

      <FaqSection aria-labelledby={`${titleId}-faq`}>
        <H3 id={`${titleId}-faq`}>{BNPL_DRAWER_FAQ_HEADING}</H3>
        {faq.map((item, index) => (
          <Accordion key={item.key} title={item.question} noBorder={index === faq.length - 1}>
            {item.answer}
          </Accordion>
        ))}
      </FaqSection>

      <OnboardingSupportHint bottomSpacing="none" />
    </BottomDrawer>
  );
}
